import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ActionButton, DialogFooter, IDialogProps, ITextField, PrimaryButton, TextField } from '@fluentui/react'
import LegacyBaseDialog from '../LegacyBaseDialog'
import { Formik, useFormikContext } from 'formik'
import * as Yup from 'yup'
import { DocumentOverviewItemDto, IDocumentStackItemDto, IFolderOverviewItemDto } from '../../../config/documentConfig'
import Form from '../../Form/Form'
import { DocumentNodeType, getDocumentNameFromPath } from '../../../helpers/documentHelper'
import FileDropdown from '../../Dropdown/File/FileDropdown'
import DocumentDropdown, { DocumentSelectionType as DropdownSelectionType } from '../../Dropdown/DocumentDropdown'
import { formatInputDocumentName } from '../../../helpers/regexHelper'
import useAxios from '../../../hooks/useAxios'
import { Destination } from '../../../context/AxiosContext'
import { ComboBoxOption } from '../../../components/Dropdown/BaseDropdown'
import useFileDocuments from '../../../hooks/file/useFileDocuments'
import { DocumentSelectionType } from '../../CommandBar/Document/DocumentCommandBar'
import { AxiosPromise, AxiosResponse } from 'axios'
import { normalizeAxiosError, NormalizedError } from '../../../reducers/errorReducer'
import { useTranslation } from 'react-i18next'

type Values = {
    name: string
    parentReference?: { id: string }
}

type Props = IDialogProps & {
    action: DocumentMutationAction
    folders: IFolderOverviewItemDto
    fileReference: string
    selectedItems: DocumentOverviewItemDto[]
    selectionType: DocumentSelectionType
    reset: (stack?: IDocumentStackItemDto[]) => void
}

type FormContainerProps = Props & {
    globalErrors: NormalizedError[]
    setStack: (stack: IDocumentStackItemDto[]) => void
}

export enum DocumentMutationAction {
    Rename = 'RENAME',
    Copy = 'COPY',
    Move = 'MOVE',
}

const validationSchema = (props: Props) =>
    Yup.object().shape({
        name: Yup.string()[isSingleNode(props.selectionType) ? 'required' : 'optional'](
            'Er is geen bestandsnaam ingevuld',
        ),
        parentReference: Yup.object().shape({
            id: Yup.string().required('Er is geen map geselecteerd'),
        }),
    })

const isSingleNode = (selectionType: DocumentSelectionType): boolean =>
    [DocumentSelectionType.Document, DocumentSelectionType.Folder].includes(selectionType)

const getStack = (item: DocumentOverviewItemDto): IDocumentStackItemDto[] =>
    item.type === DocumentNodeType.Folder ? [...item.stack, { id: item.id, name: item.name }] : item.stack

function FormContainer(props: FormContainerProps): ReactElement {
    const { values, setFieldValue } = useFormikContext<Values>()
    const { t: translate } = useTranslation()

    const nameRef = useRef<ITextField>()
    const [fileReference, setFileReference] = useState<string>(props.fileReference)
    const fileDocuments = useFileDocuments(props.fileReference !== fileReference ? fileReference : undefined)

    const folders = useMemo<IFolderOverviewItemDto>(() => {
        if (props.fileReference === fileReference) {
            return props.folders
        } else if (fileDocuments.loaded && !fileDocuments.loading) {
            return fileDocuments.data
        } else {
            return undefined
        }
    }, [fileReference, fileDocuments.data, fileDocuments.loading])

    useEffect(() => {
        const getId = (actionType: DocumentMutationAction, selectionType: DocumentSelectionType): string => {
            if (actionType === DocumentMutationAction.Rename) {
                return props.selectedItems[0].parentId
            } else if (selectionType === DocumentSelectionType.Folder && folders) {
                return folders.id
            } else if (selectionType === DocumentSelectionType.Document && props.fileReference === fileReference) {
                return props.selectedItems[0].parentId
            }
            return ''
        }

        setFieldValue('parentReference', { id: getId(props.action, props.selectionType) })
    }, [fileReference, folders])

    return (
        <Form globalErrors={props.globalErrors}>
            {isSingleNode(props.selectionType) && (
                <TextField
                    id="dialog-document-name"
                    required
                    autoFocus
                    value={values.name}
                    label={translate('file.pivot.documents.mutate.form.name')}
                    onChange={(_, value?: string) => setFieldValue('name', formatInputDocumentName(value))}
                    onFocus={() => {
                        nameRef.current?.select()
                        nameRef.current?.setSelectionEnd(getDocumentNameFromPath(values.name).length)
                    }}
                />
            )}
            {props.action !== DocumentMutationAction.Rename && (
                <FileDropdown
                    idProp="reference"
                    selectedKey={fileReference}
                    onChange={(_, option?: ComboBoxOption) => setFileReference(option.key.toString())}
                />
            )}
            {props.action !== DocumentMutationAction.Rename && (
                <DocumentDropdown
                    label={translate('file.pivot.documents.mutate.form.parentReference')}
                    placeholder={translate('file.pivot.documents.mutate.form.folder')}
                    required={true}
                    data={folders}
                    autoFocus={props.selectedItems.length !== 1}
                    selectedKey={values.parentReference.id}
                    selectionType={DropdownSelectionType.FolderOnly}
                    exclude={
                        props.selectionType === DocumentSelectionType.Folder &&
                        props.action === DocumentMutationAction.Move &&
                        props.selectedItems.map(item => item.id)
                    }
                    includeRoot={props.selectionType === DocumentSelectionType.Folder}
                    onChange={(_, option?: ComboBoxOption<DocumentOverviewItemDto>) => {
                        props.setStack(getStack(option.data))
                        setFieldValue('parentReference', { id: option.key.toString() })
                    }}
                />
            )}
            <DialogFooter>
                <PrimaryButton
                    text={translate(`file.pivot.documents.mutate.${props.action.toLowerCase()}.button`)}
                    type="submit"
                />
                <ActionButton text={translate(`action.cancel`)} onClick={props.onDismiss} />
            </DialogFooter>
        </Form>
    )
}

export default function MutateDocumentDialog(props: Props): ReactElement {
    const [globalErrors, setGlobalErrors] = useState<NormalizedError[]>()
    const [stack, setStack] = useState<IDocumentStackItemDto[]>(
        isSingleNode(props.selectionType) ? getStack(props.selectedItems[0]) : [],
    )
    const axios = useAxios()
    const submit = useCallback(
        (values: Values, item: DocumentOverviewItemDto): AxiosPromise =>
            axios.request({
                method: props.action === DocumentMutationAction.Copy ? 'post' : 'patch',
                url: props.action === DocumentMutationAction.Copy ? `/items/${item.id}/copy` : `/items/${item.id}`,
                baseURL: Destination.Documents,
                headers: { SuppressErrors: 'true' },
                data: { ...values, name: isSingleNode(props.selectionType) ? values.name : item.name },
                params: { '@microsoft.graph.conflictBehavior': 'rename' },
            }),
        [props.action, props.selectionType],
    )

    return (
        <LegacyBaseDialog {...props}>
            <Formik<Values>
                initialValues={{
                    name: props.selectedItems.length === 1 ? props.selectedItems[0].name : '',
                    parentReference: { id: isSingleNode(props.selectionType) ? props.selectedItems[0].parentId : '' },
                }}
                validate={() => setGlobalErrors(undefined)}
                validateOnChange={false}
                validateOnBlur={false}
                validationSchema={validationSchema(props)}
                onSubmit={(values: Values) => {
                    setGlobalErrors(undefined)
                    const isRejected = (result): result is PromiseRejectedResult => result.status === 'rejected'
                    return Promise.allSettled(props.selectedItems.map(item => submit(values, item))).then(
                        (results: PromiseSettledResult<AxiosResponse>[]) => {
                            if (results.some(isRejected)) {
                                setGlobalErrors(
                                    [...results.entries()]
                                        .filter(([, result]) => isRejected(result))
                                        .map(([i, result]: [number, PromiseRejectedResult]) => ({
                                            ...normalizeAxiosError(result.reason),
                                            source: props.selectedItems[i].name,
                                        })),
                                )
                            } else {
                                // Reset all affected folders.
                                // Deduplication algorithm courtesy of https://stackoverflow.com/a/64791605/862825
                                const stacks = [...props.selectedItems.map(item => item.stack), stack]
                                const stackIds = stacks.map(stack => stack[stack.length - 1].id)
                                stacks
                                    .filter((stack, i: number) => !stackIds.includes(stack[stack.length - 1].id, i + 1))
                                    .forEach(props.reset)

                                props.onDismiss()
                            }
                        },
                    )
                }}
            >
                <FormContainer globalErrors={globalErrors} setStack={setStack} {...props} />
            </Formik>
        </LegacyBaseDialog>
    )
}
